import {
  ImageDetails,
  ImageGenerator,
  ImagesMap,
  ImageGenerationParams,
  UriData,
  UploadImagesDetails,
  PromptsData,
  UploadEventHandler,
  ImageGeneratorOptions,
  TEXT_ON_IMAGE_FLOWS,
  ImageResolution,
  ImageUploadHandlers,
  GetImagesParams,
} from './types';
import { CompStructure } from '@wix/document-services-types';
import {
  createAiMediaData,
  getGroupedMapByCropType,
  getImagesDataFromStructure,
  getImagesDescriptions,
  getIsAiImageNSFWBlacklisted,
  getLastNMapItems,
  getScenePromptId,
  getStaticUriFromBlob,
  getStructurePromptId,
  getUploadImagesDataFromStructure,
  isValidUrlFormat,
} from './utils';
import { getImageFromServerless, getPhotoTheme } from './fetchDataUtils';
import { PoolStorageManager } from '../poolStorage/poolStorageManager';
import { UploadExternalUrlFn, UploadFileFn } from '../types';
import { getTextOnImageFlowPromptHub } from './textOnImage/fetchDataUtils';
import { getImageByFlow } from './textOnImage/utils';
import {
  AiMediaData,
  PresetContentFieldRole,
} from '@wix/editor-content-injector';
import { DEFAULT_IMAGE_ASPECT_RATIO, DEFAULT_IMAGE_RESOLUTION } from './consts';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { PromptGeneratorQueuer } from './PromptGenerator';
import { HttpClient } from '@wix/http-client';

export class AiImageGenerator implements ImageGenerator {
  private imagesStorageManager = new PoolStorageManager<ImageDetails>();
  private readonly promptGenerator = new PromptGeneratorQueuer();
  private uriData: UriData = new Map();

  constructor(private readonly options?: ImageGeneratorOptions) {}

  private async createImagesFactory(
    imagesAmount: number,
    imageGenerationParams: ImageGenerationParams,
    sectionName: string,
    imagesMap: ImagesMap,
    groupName: string,
    imageUploadHandlers: ImageUploadHandlers,
  ): Promise<ImageDetails[]> {
    const lastItems = getLastNMapItems(imagesMap, imagesAmount);
    const eventPayload = {
      amount: imagesAmount.toString(10),
      bunch: groupName,
    };

    imageGenerationParams?.onGenerationEvent?.({
      type: 'onBunchGenerationStart',
      payload: eventPayload,
    });

    const isTextOnImageFlow =
      groupName.includes(PresetContentFieldRole.TextOnImage) &&
      this.options?.isAiTextOnImageOpen;
    if (isTextOnImageFlow) {
      return this.getTextOnImagePhotos(
        imageGenerationParams,
        lastItems,
        sectionName,
        imageUploadHandlers,
      );
    }

    const promptIds = await this.getPromptsIds(
      imageGenerationParams,
      sectionName,
      lastItems,
      groupName,
    );
    const newImages: ImageDetails[] = [];
    if (!promptIds) return [];

    await Promise.all(
      Object.entries(promptIds).map(async ([compId, promptKey]) => {
        const imageItem = lastItems.get(compId) as ImageDetails;
        const imageResolution =
          { height: imageItem.height, width: imageItem.width } ||
          DEFAULT_IMAGE_RESOLUTION;
        const aspectRatio = imageItem.aspectRatio || DEFAULT_IMAGE_ASPECT_RATIO;
        const data = await this.generateImage(
          imageGenerationParams,
          compId,
          promptKey,
          imageResolution,
          aspectRatio,
          groupName,
        );
        if (!data?.uri) return;
        newImages.push({ ...imageItem, localUri: data.uri, promptKey });
      }),
    );

    imageGenerationParams?.onGenerationEvent?.({
      type: 'onBunchGenerationSuccess',
      payload: eventPayload,
    });

    return newImages;
  }

  private getPromptsIds = async (
    imageGenerationParams: ImageGenerationParams,
    sectionName: string,
    imagesMap: ImagesMap,
    groupName: string,
  ): Promise<PromptsData | undefined> => {
    const scenePromptVer = getScenePromptId(sectionName, groupName);
    const structurePromptVer = getStructurePromptId(sectionName);
    const imageDescriptions = getImagesDescriptions(imagesMap);
    const { siteDescription, businessTerm, msId, msInstance } =
      imageGenerationParams;

    try {
      const res = await this.promptGenerator.generate(
        {
          sectionName,
          scenePromptVer,
          structurePromptVer,
          siteDescription,
          businessTerm,
          msId,
          msInstance,
        },
        imageDescriptions,
        this.options?.interactionStarted,
        this.options?.interactionEnded,
      );

      return res;
    } catch (err) {
      console.error(err as string);
    }
  };

  private generateImage = async (
    imageGenerationParams: ImageGenerationParams,
    compId: string,
    promptKey: string,
    imageResolution: ImageResolution,
    aspectRatio: string,
    bunchName: string,
  ): Promise<{ uri: string } | undefined> => {
    const { msInstance, businessTermId } = imageGenerationParams;
    const eventPayload = {
      id: compId,
      bunch: bunchName,
      details: JSON.stringify({
        ...imageResolution,
      }),
    };

    try {
      imageGenerationParams?.onGenerationEvent?.({
        type: 'onSingleGenerationStart',
        payload: eventPayload,
      });

      const isAiImageNSFWBlacklisted =
        getIsAiImageNSFWBlacklisted(businessTermId);

      const image = await getImageFromServerless({
        msInstance,
        promptKey,
        imageResolution,
        aspectRatio,
        compId,
        isAiImageNSFWBlacklisted,
      });
      if (!image) {
        console.error(`Failed to generate image for compId: ${compId}.`);
        return;
      }
      imageGenerationParams?.onGenerationEvent?.({
        type: 'onSingleGenerationSuccess',
        payload: eventPayload,
      });
      return { uri: image.url };
    } catch (error) {
      imageGenerationParams?.onGenerationEvent?.({
        type: 'onSingleGenerationError',
        payload: {
          ...eventPayload,
          errn: error?.toString() ?? '',
        },
      });
      throw new Error(
        `Failed to generate image for compId: ${compId}. Error: ${error}`,
      );
    }
  };

  private getTextOnImagePhotos = async (
    imageGenerationParams: ImageGenerationParams,
    imagesMap: ImagesMap,
    sectionName: string,
    imageUploadHandlers: ImageUploadHandlers,
  ): Promise<ImageDetails[]> => {
    if (!imagesMap.size) return [];

    const {
      msInstance,
      businessTerm,
      sectionId,
      siteDescription: siteDescriptionFromParams,
      onGenerationEvent,
    } = imageGenerationParams;
    const siteDescription = siteDescriptionFromParams ?? '';

    const httpClient = new HttpClient({
      headers: { Authorization: msInstance },
    });

    const photoTheme = await getPhotoTheme(httpClient);
    const commonParams = {
      photoTheme,
      businessTerm,
      siteDescription,
      sectionId,
    };

    const generateTextOnImage = async (
      imageDetails: ImageDetails,
    ): Promise<ImageDetails | null> => {
      const { allowedFlows = [TEXT_ON_IMAGE_FLOWS.SKY] } = imageDetails;
      const ALLOWED_STYLES = allowedFlows.join(',');

      const { flow, brightness } = await getTextOnImageFlowPromptHub(
        httpClient,
        {
          ...commonParams,
          allowedStyles: ALLOWED_STYLES,
        },
      );

      if (!ALLOWED_STYLES.includes(flow)) return null;

      try {
        const response = await getImageByFlow(
          httpClient,
          flow,
          sectionName,
          {
            ...commonParams,
            brightness,
          },
          imageUploadHandlers,
        );

        if (!response) {
          return imageDetails;
        }
        const { imageUrl, controlImageUrl, photoScene } = response;
        onGenerationEvent({
          type: 'onTextOnImageGenerationEnd',
          payload: {
            flow,
            imageUrl,
            controlImageUrl,
            photoScene,
            details: `brightness:${brightness}`,
          },
        });
        return { ...imageDetails, localUri: imageUrl };
      } catch (error) {
        onGenerationEvent({
          type: 'onSingleGenerationError',
          payload: {
            id: '',
            bunch: 'textOnImage',
            details: JSON.stringify({
              flow,
              sectionName,
              ...commonParams,
              brightness,
            }),
            errn: error?.toString() ?? '',
          },
        });
        return imageDetails;
      }
    };

    const imageDetailsResults = await Promise.all(
      Array.from(imagesMap.values()).map(generateTextOnImage),
    );

    return imageDetailsResults.filter(
      (details): details is ImageDetails => details !== null,
    );
  };

  public async getImages({
    sectionName,
    imagesMap,
    imageGenerationParams,
    imageUploadHandlers,
  }: GetImagesParams): Promise<ImagesMap | undefined> {
    if (imagesMap.size === 0) {
      return;
    }

    const groupedMapByCropType = getGroupedMapByCropType(
      imagesMap,
      sectionName,
    );
    const images: ImagesMap = new Map();
    const promises: Promise<{
      groupName: string;
      generatedImages: Record<string, ImageDetails>;
    }>[] = [];

    for (const [groupName, cropTypeImagesMap] of groupedMapByCropType) {
      const storage =
        this.imagesStorageManager.getStorage(groupName) ??
        this.imagesStorageManager.createStorage(groupName);

      const promise = storage
        .create(
          Array.from(cropTypeImagesMap.keys()),
          async (amount: number) => {
            return this.createImagesFactory(
              amount,
              imageGenerationParams,
              sectionName,
              cropTypeImagesMap,
              groupName,
              imageUploadHandlers,
            );
          },
        )
        .then((generatedImages) => ({ groupName, generatedImages }));

      promises.push(promise);
    }
    const results = await Promise.all(promises);

    for (const { generatedImages } of results) {
      Object.entries(generatedImages).forEach(([compId, imageDetails]) => {
        images.set(compId, imageDetails);
        if (imageDetails.localUri) {
          this.uriData.set(compId, imageDetails.localUri);
        }
      });
    }

    return images;
  }

  public getImagesByStructure(
    sectionName: string,
    structure: CompStructure,
    imageGenerationParams: ImageGenerationParams,
    imageUploadHandlers: ImageUploadHandlers,
  ): Promise<ImagesMap | undefined> {
    const imagesMap = getImagesDataFromStructure(structure);
    return this.getImages({
      sectionName,
      imagesMap,
      imageGenerationParams,
      imageUploadHandlers,
    });
  }

  public uploadImagesForStructure = async (
    uploadImageUrlFn: UploadExternalUrlFn,
    sectionStructure: CompStructure,
    pageName: string,
    sectionTitle: string,
    tempMetaSiteInstance: string,
    eventsHanlder?: UploadEventHandler,
    fileUploadFn?: UploadFileFn,
  ): Promise<AiMediaData> => {
    const imagesInStructure = await getUploadImagesDataFromStructure(
      sectionStructure,
      pageName,
      sectionTitle,
    );
    const filesToUpload = imagesInStructure.filter(
      ([_, imageDetails]) =>
        imageDetails.hasOwnProperty('localUri') &&
        isValidUrlFormat(imageDetails.localUri!),
    );

    const eventPayload = {
      amount: filesToUpload.length.toString(10),
      bunch: `${pageName}_${sectionTitle}`,
    };

    eventsHanlder?.({ type: 'onBunchUploadStart', payload: eventPayload });

    const uploadPromises = filesToUpload.map(
      async ([compId, data]): Promise<UploadImagesDetails> => {
        let { staticUri } = data;
        if (staticUri) {
          return [compId, data];
        }

        const itemEventPayload = {
          ...eventPayload,
          compId,
        };

        eventsHanlder?.({
          type: 'onSingleUploadStart',
          payload: itemEventPayload,
        });

        const { localUri, displayName } = data;
        if (!localUri || !displayName) {
          throw new Error(
            `File doesn't exist yet for image with compId ${compId}`,
          );
        }

        try {
          if (localUri.startsWith('blob') && fileUploadFn) {
            staticUri = await getStaticUriFromBlob(
              localUri,
              displayName,
              fileUploadFn,
            );
          } else {
            staticUri = await uploadImageUrlFn(
              { url: localUri, name: displayName },
              tempMetaSiteInstance,
            );
          }
        } catch (error) {
          ErrorReporter.captureException(error);
          throw new Error(
            `Broken image upload for image with compId ${compId}`,
          );
        }

        eventsHanlder?.({
          type: 'onSingleUploadSuccess',
          payload: {
            ...itemEventPayload,
            imageUrl: staticUri,
          },
        });

        return [
          compId,
          {
            ...data,
            staticUri,
          },
        ];
      },
    );

    const uploadedEntries = await Promise.all(uploadPromises);
    const aiMediaData = createAiMediaData(Object.fromEntries(uploadedEntries));
    eventsHanlder?.({ type: 'onBunchUploadSuccess', payload: eventPayload });
    return aiMediaData;
  };

  /**
   * Makes All previously generated images available for reuse
   */
  public async freeImages() {
    this.imagesStorageManager.manageEntities((storage) => storage.freeAll());
  }

  /**
   * Clean's up images from the storages so next generation
   * will be 100% uniq.
   */
  public async flushImages() {
    this.imagesStorageManager.manageEntities((storage, key) => {
      storage.flush(() => {});
      this.imagesStorageManager.removeStorage(key);
    });
  }

  public async clearUriData() {
    this.uriData.forEach(
      (uri) => uri && uri.startsWith('blob') && URL.revokeObjectURL(uri),
    );
    this.uriData.clear();
  }
}
